import {
  BaseModel,
  Watchable,
  useInScopeModel,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { emitChatBotConfigUpdated } from '@cotera/client/app/etc/emit-chat-bot-config-updated';
import type { WatchablesFactory } from '@cotera/client/app/v1/watchables/watchables-factory';

export type SlackBotSettingsState = {
  loaded: boolean;
  installed: boolean;
  teamId?: string;
  channels: Array<{
    channelId: string;
    channelName: string;
    agentId: string | null;
  }>;
  channelsError?: string;
};

const EMPTY_SLACK_BOT_SETTINGS_STATE: SlackBotSettingsState = {
  loaded: false,
  installed: false,
  channels: [],
};

function slackBotSettingsSignature(a: SlackBotSettingsState): string {
  return `${a.loaded}|${a.installed}|${a.teamId ?? ''}|${
    a.channelsError ?? ''
  }|${a.channels
    .map((channel) => `${channel.channelId}:${channel.agentId ?? ''}`)
    .join(';')}`;
}

type SlackBotSettingsModelProps = {
  orgId: string;
  client: Pick<TenantedClient, 'toolsV2'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeChatBotConfigUpdatedWatchable'
  >;
};

export class SlackBotSettingsModel extends BaseModel {
  private readonly orgId: string;
  private readonly client: Pick<TenantedClient, 'toolsV2'>;
  private readonly _configWatchable: ReadonlyEventWatchable<SlackBotSettingsState>;
  private readonly _status: ReadonlyWatchable<'loading' | 'ready'>;
  private readonly _pendingChannelId = Watchable.fromValue<string | null>(null);

  constructor(props: SlackBotSettingsModelProps) {
    super('slack-bot-settings');
    this.orgId = props.orgId;
    this.client = props.client;

    this._configWatchable =
      props.watchablesFactory.makeChatBotConfigUpdatedWatchable<SlackBotSettingsState>(
        {
          runOnMount: true,
          initialValue: EMPTY_SLACK_BOT_SETTINGS_STATE,
          equalityFn: (left, right) =>
            slackBotSettingsSignature(left) ===
            slackBotSettingsSignature(right),
          filter: (payload) =>
            payload?.orgId === this.orgId && payload.bot === 'slack',
          fn: async () => this.fetchState(),
        }
      );

    this._status = Watchable.from((get) => {
      return get(this._configWatchable).loaded ? 'ready' : 'loading';
    });
  }

  get config(): ReadonlyWatchable<SlackBotSettingsState> {
    return this._configWatchable;
  }

  get status(): ReadonlyWatchable<'loading' | 'ready'> {
    return this._status;
  }

  get pendingChannelId(): ReadonlyWatchable<string | null> {
    return this._pendingChannelId;
  }

  private async fetchState(): Promise<SlackBotSettingsState> {
    const installation = Assert.assertOk(
      await this.client.toolsV2.getSlackBotInstallation({})
    );

    if (installation.installed !== true) {
      return {
        loaded: true,
        installed: false,
        channels: [],
      };
    }

    const channelsResult = await this.client.toolsV2.listSlackChannelAgents({});
    if (channelsResult.isErr()) {
      const channelsError =
        channelsResult.error.t === 'application'
          ? channelsResult.error.err.message
          : 'Failed to load Slack channel agents';
      return {
        loaded: true,
        installed: true,
        teamId: installation.teamId,
        channels: [],
        channelsError,
      };
    }

    return {
      loaded: true,
      installed: true,
      teamId: installation.teamId,
      channels: channelsResult.value.channels,
    };
  }

  async refresh(): Promise<void> {
    await this._configWatchable.refresh();
  }

  async setChannelAgent(
    channelId: string,
    agentId: string,
    confirmShare = false
  ): Promise<{ needsShare: boolean }> {
    this._pendingChannelId.set(channelId);
    try {
      const result = Assert.assertOk(
        await this.client.toolsV2.setSlackChannelAgent({
          channelId,
          agentId,
          confirmShare,
        })
      );
      if (result.status === 'needs-share') {
        return { needsShare: true };
      }
      emitChatBotConfigUpdated(this.orgId, 'slack');
      await this._configWatchable.refresh();
      return { needsShare: false };
    } finally {
      this._pendingChannelId.set(null);
    }
  }

  async clearChannelAgent(channelId: string): Promise<void> {
    this._pendingChannelId.set(channelId);
    try {
      Assert.assertOk(
        await this.client.toolsV2.clearSlackChannelAgent({ channelId })
      );
      emitChatBotConfigUpdated(this.orgId, 'slack');
      await this._configWatchable.refresh();
    } finally {
      this._pendingChannelId.set(null);
    }
  }

  dispose(): void {
    this._configWatchable.unsubscribe();
  }
}

export const useSlackBotSettingsModel = (): SlackBotSettingsModel => {
  const model = useInScopeModel(SlackBotSettingsModel);
  if (model === null) {
    throw new Error('SlackBotSettingsModel not found in scope');
  }
  return model;
};
